/**
 * Trustpilot Review Widget Tab - Media slider (All/Images/Videos) and modal.
 * Swiper API based slider component.
 */

class TrustpilotReviewWidgetTab extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.currentItems = [];
    this.modal = null;
    this.sliderElement = null;
    this.swiper = null;
    this.attachmentsLoaded = false;

    /** ADA Compliance: Configurable announcement messages */
    this.announcements = {
      tabChanged: (tabName) => `Showing ${tabName}.`,
    };
  }

  connectedCallback() {
    const checkDependencies = () => {
      if (typeof Swiper !== 'undefined' || window.Swiper) {
        this.init();
      } else {
        setTimeout(checkDependencies, 100);
      }
    };

    checkDependencies();
  }

  /** Resolve slider and modal refs; if slider visible init content, else wait for tab open. */
  init() {
    setTimeout(() => {
      this.sliderElement = this.querySelector('[data-trustpilot-slider]');
      const parentWidget = this.closest('trustpilot-review-widget');
      this.modal = parentWidget
        ? parentWidget.querySelector('[data-tp-modal]')
        : document.querySelector('[data-tp-modal]');

      if (!this.sliderElement) return;

      if (this.swiper || this.sliderElement.classList.contains('swiper-initialized')) {
        this.setupFocus();
        this.checkArrows();
        return;
      }

      /** Check if review widget tab element is visible */
      if (this.offsetParent !== null) {
        this.initializeContent();
      } else {
        this.waitForTabOpen();
      }
    }, 0);
  }

  /** When tab is hidden, attach one-time listener to desktop tab or mobile accordion to run init on open. */
  waitForTabOpen() {
    const tabPanel = this.closest('[role="tabpanel"]');
    const accordionSection = this.closest('.prod-tabcordion--entry, .plus-icon-accordion');

    let tab = null;
    let mobileAccordion = null;

    if (tabPanel) {
      const panelId = tabPanel.id;
      tab = document.querySelector(`[aria-controls="${panelId}"]`);
    }

    if (accordionSection) {
      mobileAccordion = accordionSection.querySelector('.mobile-accordion-controller');
    }

    const handleClick = () => {
      if (tab) tab.removeEventListener('click', handleClick);
      if (mobileAccordion) mobileAccordion.removeEventListener('click', handleClick);

      setTimeout(() => {
        if (this.swiper) {
          this.swiper.update();
          this.setupFocus();
          this.checkArrows();
        } else {
          this.initializeContent();
        }
      }, 100);
    };

    if (tab) tab.addEventListener('click', handleClick, { once: true });
    if (mobileAccordion) mobileAccordion.addEventListener('click', handleClick, { once: true });
  }

  /** Load attachments, toggle section visibility, setup modal, init Swiper & tabs. */
  async initializeContent() {
    await this.loadAttachments();

    const skeleton = this.querySelector('[data-skeleton-media-items]');
    if (skeleton) skeleton.classList.add('hidden');

    const mediaCount = this.sliderElement.querySelectorAll('[data-trustpilot-item]').length;
    this.toggleSectionVisibility(mediaCount > 0);
    if (mediaCount <= 1) this.setupFocus();

    this.initModalLogic();

    const widget = this.closest('trustpilot-review-widget');
    if (widget && typeof widget.toggleMediaSkeleton === 'function') {
      widget.toggleMediaSkeleton(false);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.shouldInitSlider(mediaCount)) {
          this.initSlider();
        }
        this.initTabs();
        this.syncAriaSelectedState();
      });
    });
  }

  /** Fetch attachments for all reviews on card; add items to slider; update card badges. */
  async loadAttachments() {
    if (this.attachmentsLoaded) return;
    const parentWidget = this.closest('trustpilot-review-widget');
    if (!parentWidget) return;

    let reviewCards = Array.from(parentWidget.querySelectorAll('.trustpilot-item[data-review-id]'));
    if (!reviewCards.length) {
      const mainWidget = document.querySelector('trustpilot-review-widget');
      if (mainWidget) {
        reviewCards = Array.from(mainWidget.querySelectorAll('.trustpilot-item[data-review-id]'));
      }
    }

    if (!reviewCards.length) {
      this.attachmentsLoaded = true;
      return;
    }

    const results = await Promise.all(
      reviewCards.map(async (card) => {
        const reviewId = card.getAttribute('data-review-id');
        if (!reviewId) return null;

        try {
          const attachments = await window.trustpilotAPI.fetchAttachments(reviewId);
          return { card, reviewId, attachments };
        } catch (e) {
          return null;
        }
      })
    );

    const fragment = document.createDocumentFragment();

    results.forEach((res) => {
      if (!res || !res.attachments) return;

      const { card, reviewId, attachments } = res;
      const images = attachments.images || [];
      const videos = attachments.videos || [];

      if (images.length > 0 || videos.length > 0) {
        card.setAttribute('data-has-media', 'true');
        card.classList.remove('no-media');

        let container = card.querySelector('[data-tp-card-attachments]');
        if (!container) {
          const content = card.querySelector('.trustpilot-content') || card;
          container = document.createElement('div');
          container.className = 'tp-card-attachments-list';
          container.setAttribute('data-tp-card-attachments', '');
          content.appendChild(container);
        }
        container.innerHTML = '';

        const reviewName =
          card.querySelector('.trustpilot-consumer-name')?.textContent?.trim() || 'Reviewer';
        const reviewDate =
          card.querySelector('.trustpilot-date')?.textContent?.trim() || '';

        images.forEach((img) => {
          const item = this.createMediaItem({
            type: 'image',
            url: img.processedUrl || img.originalUrl || img.url,
            thumbUrl: img.processedUrl || img.thumbUrl || img.url,
            reviewId,
            reviewName,
            reviewDate,
            caption: img.caption || '',
          });
          fragment.appendChild(item);

          const badge = document.createElement('div');
          badge.className = 'tp-card-attachment-thumb tp-media-type-image';
          badge.setAttribute('data-tp-card-media-item', '');
          badge.setAttribute('data-review-id', reviewId);
          badge.innerHTML = `<img src="${img.processedUrl || img.thumbUrl || img.url}" alt="Attachment for ${reviewName}" loading="lazy" />`;
          container.appendChild(badge);
        });

        videos.forEach((vid) => {
          const item = this.createMediaItem({
            type: 'video',
            url: vid.originalUrl || vid.url,
            thumbUrl: vid.thumbnailUrl || vid.thumbUrl || vid.url,
            reviewId,
            reviewName,
            reviewDate,
            caption: vid.caption || '',
          });
          fragment.appendChild(item);

          const badge = document.createElement('div');
          badge.className = 'tp-card-attachment-thumb tp-media-type-video';
          badge.setAttribute('data-tp-card-media-item', '');
          badge.setAttribute('data-review-id', reviewId);
          badge.innerHTML = `<img src="${vid.thumbnailUrl || vid.thumbUrl || vid.url}" alt="Video attachment for ${reviewName}" loading="lazy" /><span class="tp-video-icon-badge" aria-hidden="true">&#9654;</span>`;
          container.appendChild(badge);
        });
      }
    });

    if (fragment.children.length > 0) {
      this.sliderElement.innerHTML = '';
      this.sliderElement.appendChild(fragment);

      this.sliderElement.querySelectorAll('img[data-tp-media-src]').forEach((img) => {
        const src = img.getAttribute('data-tp-media-src');
        if (src) {
          img.src = src;
          img.removeAttribute('data-tp-media-src');
        }
      });
    }

    this.attachmentsLoaded = true;
    this.initResizeListener();
  }

  /** Re-evaluates slider init on window resize when item count / viewport breakpoint changes. */
  initResizeListener() {
    let resizeInitT = null;
    window.addEventListener('resize', () => {
      if (resizeInitT) clearTimeout(resizeInitT);
      resizeInitT = setTimeout(() => {
        if (this.swiper) {
          this.swiper.update();
          return;
        }
        const count = this.sliderElement?.querySelectorAll('[data-trustpilot-item]').length ?? 0;
        if (count > 0 && this.shouldInitSlider(count)) {
          this.initSlider();
        }
      }, 150);
    });
  }

  /** Called by widget after filter/sort: destroy swiper, reload attachments, re-init tabs and Swiper. */
  async refreshContent() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.attachmentsLoaded = false;
    await this.loadAttachments();

    const skeleton = this.querySelector('[data-skeleton-media-items]');
    if (skeleton) skeleton.classList.add('hidden');

    const mediaCount = this.sliderElement.querySelectorAll('[data-trustpilot-item]').length;
    this.toggleSectionVisibility(mediaCount > 0);
    if (mediaCount <= 1) this.setupFocus();

    this.initModalLogic();

    const section = this.closest('.trustpilot-widget-tab-section');
    if (section) {
      const emptyImages = section.querySelector('[data-tp-media-empty-state="images"]');
      const emptyVideos = section.querySelector('[data-tp-media-empty-state="videos"]');
      this.sliderElement.classList.remove('hidden');
      this.sliderElement.removeAttribute('aria-hidden');
      if (emptyImages) emptyImages.classList.add('hidden');
      if (emptyVideos) emptyVideos.classList.add('hidden');
    }

    const widget = this.closest('trustpilot-review-widget');
    if (widget && typeof widget.toggleMediaSkeleton === 'function') {
      widget.toggleMediaSkeleton(false);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.shouldInitSlider(mediaCount)) {
          this.initSlider();
        }
        this.initTabs();
        this.syncAriaSelectedState();
      });
    });
  }

  /** Init Swiper when item count exceeds device limit (data-item-desktop / tablet / mobile). */
  initSlider() {
    if (this.swiper) return;
    const items = Array.from(this.sliderElement.querySelectorAll('[data-trustpilot-item]'));
    if (!this.shouldInitSlider(items.length)) return;

    if (this.sliderElement.offsetWidth <= 0) {
      requestAnimationFrame(() => this.initSlider());
      return;
    }

    const slidesDesktop = parseInt(this.getAttribute('data-item-desktop')) || 6;
    const slidesTablet = parseInt(this.getAttribute('data-item-tablet')) || 4;
    const slidesMobile = parseInt(this.getAttribute('data-item-mobile')) || 3;

    this.sliderElement.classList.add('swiper');
    let wrapper = this.sliderElement.querySelector('.swiper-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'swiper-wrapper';
      while (this.sliderElement.firstChild) {
        wrapper.appendChild(this.sliderElement.firstChild);
      }
      this.sliderElement.appendChild(wrapper);
    }

    wrapper.querySelectorAll('[data-trustpilot-item]').forEach((item) => {
      item.classList.add('swiper-slide');
    });

    let prevBtn = this.sliderElement.querySelector('.swiper-button-prev');
    let nextBtn = this.sliderElement.querySelector('.swiper-button-next');
    if (!prevBtn) {
      prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'swiper-button-prev';
      prevBtn.setAttribute('aria-label', 'Previous review media');
      this.sliderElement.appendChild(prevBtn);
    }
    if (!nextBtn) {
      nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'swiper-button-next';
      nextBtn.setAttribute('aria-label', 'Next review media');
      this.sliderElement.appendChild(nextBtn);
    }

    const SwiperLib = window.Swiper || (typeof Swiper !== 'undefined' ? Swiper : null);
    if (!SwiperLib) {
      setTimeout(() => this.initSlider(), 100);
      return;
    }

    this.swiper = new SwiperLib(this.sliderElement, {
      slidesPerView: slidesMobile,
      spaceBetween: 12,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
      breakpoints: {
        769: { slidesPerView: slidesTablet, spaceBetween: 16 },
        1025: { slidesPerView: slidesDesktop, spaceBetween: 20 },
      },
      on: {
        init: () => {
          this.sliderElement.classList.add('swiper-initialized');
          this.setupFocus();
        },
        slideChange: () => {
          this.setupFocus();
        },
      },
    });
  }

  /** Wire All/Images/Videos tabs: filter Swiper slides and show empty state when no media for tab. */
  initTabs() {
    const section = this.closest('.trustpilot-widget-tab-section');
    if (!section) return;

    const tabs = Array.from(section.querySelectorAll('[data-trustpilot-tab]'));
    const emptyImages = section.querySelector('[data-tp-media-empty-state="images"]');
    const emptyVideos = section.querySelector('[data-tp-media-empty-state="videos"]');

    const showSlider = () => {
      this.sliderElement.classList.remove('hidden');
      this.sliderElement.removeAttribute('aria-hidden');

      if (emptyImages) emptyImages.classList.add('hidden');
      if (emptyVideos) emptyVideos.classList.add('hidden');
    };

    const showMediaEmptyState = (type) => {
      this.sliderElement.classList.add('hidden');
      this.sliderElement.setAttribute('aria-hidden', 'true');
      if (emptyImages) emptyImages.classList.toggle('hidden', type !== 'images');
      if (emptyVideos) emptyVideos.classList.toggle('hidden', type !== 'videos');
    };

    const getMediaTypeFromSlide = (slideEl) => {
      const itemInner = slideEl.querySelector('[data-tp-item-inner]');
      return itemInner ? itemInner.getAttribute('data-media-type') : null;
    };

    const applyTabFilter = (target) => {
      const slides = Array.from(this.sliderElement.querySelectorAll('.swiper-slide, [data-trustpilot-item]'));
      const imageCount = slides.filter((el) => getMediaTypeFromSlide(el) === 'image').length;
      const videoCount = slides.filter((el) => getMediaTypeFromSlide(el) === 'video').length;

      if (target === 'all') {
          showSlider();
          slides.forEach((el) => (el.style.display = ''));
          if (this.swiper) {
            this.swiper.update();
            this.swiper.slideTo(0);
        }
      } else if (target === 'images') {
        if (imageCount > 0) {
          showSlider();
          slides.forEach((el) => {
            el.style.display = getMediaTypeFromSlide(el) === 'image' ? '' : 'none';
          });
          if (this.swiper) {
            this.swiper.update();
            this.swiper.slideTo(0);
          }
        } else {
          showMediaEmptyState('images');
        }
      } else if (target === 'videos') {
        if (videoCount > 0) {
          showSlider();
          slides.forEach((el) => {
            el.style.display = getMediaTypeFromSlide(el) === 'video' ? '' : 'none';
          });
          if (this.swiper) {
            this.swiper.update();
            this.swiper.slideTo(0);
          }
        } else {
          showMediaEmptyState('videos');
        }
      }
    };

    tabs.forEach((t) => t.setAttribute('tabindex', '0'));

    tabs.forEach((tab, index) => {
      const triggerTab = () => {
        const target = tab.getAttribute('data-tab');
        const panel = section.querySelector('#trustpilot-media-panel');

        tabs.forEach((t) => {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
          t.setAttribute('tabindex', '-1');
        });

        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('tabindex', '0');

        if (panel) {
          panel.setAttribute('aria-labelledby', tab.id);
        }

        const tabName = tab.textContent.trim();
        this.announceToScreenReader(this.announcements.tabChanged(tabName));

        applyTabFilter(target);
      };

      tab.onclick = triggerTab;
      tab.onkeydown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          triggerTab();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          const nextTab = tabs[(index + 1) % tabs.length];
          nextTab.focus();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const prevTab = tabs[(index - 1 + tabs.length) % tabs.length];
          prevTab.focus();
        }
      };
    });
  }

  /** Keep tab button aria-selected in sync with active CSS class. */
  syncAriaSelectedState() {
    const section = this.closest('.trustpilot-widget-tab-section');
    if (!section) return;

    section.querySelectorAll('[data-trustpilot-tab]').forEach((tab) => {
      const isActive = tab.classList.contains('active');
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  /** Returns true if total item count > device threshold. */
  shouldInitSlider(count) {
    const slidesDesktop = parseInt(this.getAttribute('data-item-desktop')) || 6;
    const slidesTablet = parseInt(this.getAttribute('data-item-tablet')) || 4;
    const slidesMobile = parseInt(this.getAttribute('data-item-mobile')) || 3;

    const w = typeof window !== 'undefined' ? window.innerWidth : 9999;
    const limit = w > 1024 ? slidesDesktop : w > 768 ? slidesTablet : slidesMobile;

    return count > limit;
  }

  /** Toggle section container display when media items are present vs 0. */
  toggleSectionVisibility(hasMedia) {
    const section = this.closest('.trustpilot-widget-tab-section');
    if (!section) return;
    section.style.display = hasMedia ? '' : 'none';
  }

  /** Attach click delegate on slider for item modal trigger. */
  initModalLogic() {
    if (!this.sliderElement) return;

    this.sliderElement.removeEventListener('click', this._onSliderClick);

    this._onSliderClick = (e) => {
      if (e.target.closest('[data-tp-item-inner]')) {
        this.openModal(e);
      }
    };

    this.sliderElement.addEventListener('click', this._onSliderClick);
  }

  openModal(event) {
    const clickedItem = event.target.closest('[data-tp-item-inner]');
    if (!clickedItem || !window.trustpilotReviewModal) return;

    const mediaItems = Array.from(this.sliderElement.querySelectorAll('[data-tp-item-inner]'));
    const currentIndex = mediaItems.indexOf(clickedItem);

    if (currentIndex < 0) return;
    window.trustpilotReviewModal.open(this, currentIndex, mediaItems);
  }

  closeModal() {
    if (window.trustpilotReviewModal) {
      window.trustpilotReviewModal.close();
    }
  }

  nextMedia() {
    if (!this.currentItems.length) return;
    this.currentIndex = (this.currentIndex + 1) % this.currentItems.length;
    this.renderModalContent();
  }

  prevMedia() {
    if (!this.currentItems.length) return;
    this.currentIndex =
      (this.currentIndex - 1 + this.currentItems.length) % this.currentItems.length;
    this.renderModalContent();
  }

  renderModalContent() {
    const currentItem = this.currentItems[this.currentIndex];
    if (!currentItem || !this.modal) return;

    const mediaType = currentItem.getAttribute('data-media-type');
    const mediaUrl = currentItem.getAttribute('data-media-url');
    const reviewerName = currentItem.getAttribute('data-reviewer-name') || 'Reviewer';
    const reviewDate = currentItem.getAttribute('data-review-date') || '';
    const caption = currentItem.getAttribute('data-caption') || '';
    const reviewId = currentItem.getAttribute('data-review-id');

    const mediaContainer = this.modal.querySelector('[data-tp-modal-media-container]');
    const reviewerNameEl = this.modal.querySelector('[data-tp-modal-reviewer-name]');
    const reviewDateEl = this.modal.querySelector('[data-tp-modal-date]');
    const captionEl = this.modal.querySelector('[data-tp-modal-caption]');
    const counterEl = this.modal.querySelector('[data-tp-modal-counter]');
    const originalReviewEl = this.modal.querySelector('[data-tp-modal-original-review]');

    if (mediaContainer) {
      if (mediaType === 'video') {
        mediaContainer.innerHTML = `<video src="${mediaUrl}" controls autoplay playsinline style="max-width:100%; max-height:80vh;"></video>`;
      } else {
        mediaContainer.innerHTML = `<img src="${mediaUrl}" alt="Media by ${reviewerName}" style="max-width:100%; max-height:80vh; object-fit:contain;" />`;
      }
    }

    if (reviewerNameEl) reviewerNameEl.textContent = reviewerName;
    if (reviewDateEl) reviewDateEl.textContent = reviewDate;

    if (captionEl) {
      captionEl.textContent = caption;
      captionEl.classList.toggle('hidden', !caption);
    }

    if (counterEl) {
      counterEl.textContent = `${this.currentIndex + 1} of ${this.currentItems.length}`;
    }

    if (originalReviewEl && reviewId) {
      const parentWidget = this.closest('trustpilot-review-widget');
      const originalCard = parentWidget
        ? parentWidget.querySelector(`.trustpilot-item[data-review-id="${reviewId}"]`)
        : document.querySelector(`.trustpilot-item[data-review-id="${reviewId}"]`);

      if (originalCard) {
        const text =
          originalCard.querySelector('.trustpilot-text')?.innerHTML ||
          originalCard.querySelector('.trustpilot-content')?.innerHTML ||
          '';
        const stars = originalCard.querySelector('.trustpilot-stars')?.outerHTML || '';
        const title = originalCard.querySelector('.trustpilot-title')?.textContent || '';

        originalReviewEl.innerHTML = `
          <div class="tp-modal-review-card">
            <div class="tp-modal-review-header">
              ${stars}
              <strong>${title}</strong>
            </div>
            <div class="tp-modal-review-body">${text}</div>
          </div>
        `;
      } else {
        originalReviewEl.innerHTML = '';
      }
    }

    this.announceToScreenReader(
      `Viewing media ${this.currentIndex + 1} of ${this.currentItems.length} from ${reviewerName}`
    );
  }

  createMediaItem({ type, url, thumbUrl, reviewId, reviewName, reviewDate, caption }) {
    const item = document.createElement('div');
    item.className = 'trustpilot-item';
    item.setAttribute('data-trustpilot-item', '');

    const isVideo = type === 'video';
    const mediaBadge = isVideo
      ? `<span class="tp-video-play-icon" aria-hidden="true">&#9654;</span>`
      : '';

    item.innerHTML = `
      <div class="trustpilot-item-inner" data-tp-item-inner 
           data-media-type="${type}" 
           data-media-url="${url}"
           data-reviewer-name="${reviewName}"
           data-review-date="${reviewDate}"
           data-caption="${caption}"
           data-review-id="${reviewId}"
           tabindex="0"
           role="button"
           aria-label="Open ${type} by ${reviewName}">
        <div class="tp-media-wrapper">
          <img src="${thumbUrl}" alt="${type} attachment by ${reviewName}" loading="lazy" />
          ${mediaBadge}
        </div>
      </div>
    `;

    return item;
  }

  /** Accessible Focus Management */
  setupFocus() {
    if (!this.sliderElement) return;

    const allItems = Array.from(
      this.sliderElement.querySelectorAll('[data-tp-item-inner]')
    );

    allItems.forEach((el) => el.setAttribute('tabindex', '-1'));

    if (allItems.length > 0) {
      allItems[0].setAttribute('tabindex', '0');
    }

    allItems.forEach((el) => {
      if (!el.dataset.focusBound) {
        el.dataset.focusBound = 'true';

        el.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const visibleItems = Array.from(
              this.sliderElement.querySelectorAll('[data-tp-item-inner]')
            );
            const currIdx = visibleItems.indexOf(el);
            if (currIdx !== -1) {
              const nextIdx =
                e.key === 'ArrowRight'
                  ? (currIdx + 1) % visibleItems.length
                  : (currIdx - 1 + visibleItems.length) % visibleItems.length;

              visibleItems.forEach((item) => item.setAttribute('tabindex', '-1'));
              visibleItems[nextIdx].setAttribute('tabindex', '0');
              visibleItems[nextIdx].focus();
            }
          }
        });
      }
    });

    this.checkArrows();
  }

  /** Check if swiper arrows should be hidden when slides <= visible count. */
  checkArrows() {
    if (!this.sliderElement) return;
    const count = this.sliderElement.querySelectorAll('[data-trustpilot-item]').length;
    const shouldHide = !this.shouldInitSlider(count);

    const arrows = this.sliderElement.querySelectorAll('.swiper-button-next, .swiper-button-prev');
    arrows.forEach((arrow) => {
      arrow.classList.toggle('tp-arrows-hide', shouldHide);
    });
  }

  /** Announce text to screen reader live region. */
  announceToScreenReader(message) {
    let liveRegion = document.getElementById('tp-a11y-live-region');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'tp-a11y-live-region';
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    setTimeout(() => {
      liveRegion.textContent = message;
    }, 50);
  }
}

if (!customElements.get('trustpilot-review-widget-tab')) {
  customElements.define('trustpilot-review-widget-tab', TrustpilotReviewWidgetTab);
}
